import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { CycleCountApi } from '~/api/cycleCount';
import { warehouseApi } from '~/api/warehouse';
import { UserContext } from '~/contexts/UserContext';
import { Button } from '~/shadcn/components/ui/Button';
import { Combobox } from '~/shadcn/components/ui/Combobox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '~/shadcn/components/ui/Dialog';

const cycleCountApi = new CycleCountApi();

function ReplenCycleCountForm({ closeModal, onSuccess, open }) {
    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dialogContentEl, setDialogContentEl] = useState(null);
    const { user } = useContext(UserContext);

    const dialogContentRef = useCallback((node) => {
        setDialogContentEl((prev) => {
            const next = node ?? null;
            return prev === next ? prev : next;
        });
    }, []);

    const locationOptions = useMemo(
        () =>
            locations.map((location) => ({
                value: String(location.id),
                label: location.name,
            })),
        [locations],
    );

    const locationLabelByValue = useMemo(
        () => new Map(locationOptions.map((opt) => [opt.value, opt.label])),
        [locationOptions],
    );

    useEffect(() => {
        const getLocations = async () => {
            const res = await warehouseApi.getLocations(user?.facilityID, true, undefined, 'REPLENISHMENT');
            if (!res.status || !res?.data?.rows) {
                setError('Failed to get locations');
                return;
            }
            setLocations(res.data.rows);
        };

        if (open && user?.facilityID) {
            getLocations();
        }
    }, [open, user]);

    const handleCreate = async () => {
        if (isSubmitting) {
            return;
        }
        if (!selectedLocation) {
            setError('Please select a location');
            return;
        }
        setIsSubmitting(true);
        const res = await cycleCountApi.createReplenishmentLocationCycleCountRequest(user.facilityID, selectedLocation);
        if (!res.status) {
            setError(res.msg);
            setIsSubmitting(false);
            return;
        }
        setIsSubmitting(false);
        onSuccess();
    };

    return (
        <Dialog open={open} onOpenChange={closeModal}>
            <DialogContent ref={dialogContentRef}>
                <DialogHeader>
                    <DialogTitle>Create New Replenishment Cycle Count</DialogTitle>
                    <DialogDescription>Select a location to create a new cycle count request.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <Combobox
                        options={locationOptions}
                        value={selectedLocation}
                        handleSelect={(nextValue) => {
                            setError('');
                            setSelectedLocation(String(nextValue ?? ''));
                        }}
                        placeholder="Select a location"
                        disabled={!locations.length}
                        container={dialogContentEl}
                        popoverWidth="trigger"
                        className="w-full"
                        triggerClassName="w-full"
                        truncateLabel
                        commandProps={{
                            filter: (value, search) => {
                                const label = locationLabelByValue.get(String(value)) ?? '';
                                return label.toLowerCase().includes(search.toLowerCase());
                            },
                        }}
                    />
                </div>
                {error && <div style={{ color: 'red' }}>{error}</div>}
                <DialogFooter>
                    <Button variant="outline" onClick={closeModal}>
                        Cancel
                    </Button>
                    <Button onClick={handleCreate} disabled={isSubmitting}>
                        Create
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { ReplenCycleCountForm };
